import { Link } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button } from '@fluxora/ui';

import {
  classifyQuoteDraftFailure,
  meterRestartLabelKey,
  offersMeterRestart,
  offersPlanShelfReturn,
  offersPriceConfirmation,
  quoteDraftFailureCopy,
} from '../services/quote-draft-error.service';

/**
 * The quote-draft refusal, with a way out (QA-2026-08-06).
 *
 * Replaces a single unconditional alert that said "Something went wrong setting
 * up your quote. Please try again in a moment." for THREE different upstream
 * conditions, none of which retrying can fix, and offered nothing to click.
 *
 * Render-only: `services/quote-draft-error.service` decides which failure this
 * is, which copy keys it takes, and whether it may be restarted from. This file
 * chooses no words and encodes no rule.
 *
 * ── EVERY BRANCH OFFERS A HUMAN ────────────────────────────────────────────
 * The support link is unconditional on a recognised refusal, and for
 * `already-enrolled` it is the ONLY affordance — deliberately. The BFF withholds
 * the `openQuoteId` (`response-guard.ts`), so there is no "resume your
 * enrollment" link to offer, and inventing a restart that will refuse again for
 * the same reason is worse than telling the customer to talk to us.
 */
export interface QuoteDraftErrorAlertProps {
  /**
   * The BFF's RFC7807 problem `type`, read by `problemTypeOf` at the page. Bare
   * string rather than the error object so the branching rule stays in the
   * service and this component keeps no knowledge of the network seam.
   */
  readonly problemType: string | undefined;
  /**
   * The BFF's own customer-safe `detail`, when it wrote one (E-SF19 P1).
   *
   * ⚠️ IT ONLY EVER REPLACES THE **GENERIC** BODY. Where this alert recognises a
   * problem type it renders its own copy WITH an affordance attached ("try a
   * different meter", "re-enter your ESI ID") — that is strictly better than a
   * sentence, and letting a detail override it would trade a button for prose.
   * This is the fallback for a refusal we have no branch for: better than
   * "Something went wrong", worse than a route forward.
   */
  readonly serverDetail?: string | null;
  /**
   * Send the shopper back to the meter chooser. Clears the resolved candidate
   * first — otherwise the landing page renders the same meter that was just
   * refused and the "try a different meter" control changes nothing.
   */
  readonly onRestartMeter: () => void;
  /**
   * E-SF16 — "continue at the current price" on the offer path. Re-submits the
   * SAME step with `confirmPriceChange: true`; the BFF supplies the upstream's
   * confirmation token from the funnel session, so nothing is passed from here.
   *
   * Optional: the self-serve funnel cannot produce a `price-changed` refusal, so a
   * caller that never enters via an offer link has nothing to wire.
   */
  readonly onConfirmPriceChange?: () => void;
  /** True while a re-submission is in flight, so the confirm control cannot double-fire. */
  readonly isConfirming?: boolean;
  /** E-SF16 — where "back to your plans" points. Omitted → the control is not offered. */
  readonly planShelfPath?: string;
}

export function QuoteDraftErrorAlert({
  problemType,
  serverDetail = null,
  onRestartMeter,
  onConfirmPriceChange,
  isConfirming = false,
  planShelfPath,
}: QuoteDraftErrorAlertProps): JSX.Element {
  const { t } = useTranslation();

  const failure = classifyQuoteDraftFailure(problemType);
  const copy = quoteDraftFailureCopy(failure);
  const restartLabelKey = meterRestartLabelKey(failure);

  return (
    <Alert variant="danger" title={t(copy.titleKey)} role="alert">
      <div className="flex flex-col gap-3">
        {/*
          A recognised refusal keeps its own copy (it comes with an action below);
          an UNRECOGNISED one prefers the server's sentence over the generic text.
        */}
        <span>
          {copy.isGeneric && serverDetail !== null ? serverDetail : t(copy.bodyKey)}
        </span>

        {failure === 'unknown' ? null : (
          <div className="flex flex-wrap items-center gap-3">
            {offersMeterRestart(failure) && restartLabelKey !== null ? (
              <Button variant="secondary" shape="pill" size="sm" onClick={onRestartMeter}>
                {t(restartLabelKey)}
              </Button>
            ) : null}
            {/*
              E-SF16 — the price-drift confirmation. Rendered only when the SERVICE
              says this failure offers one AND the caller supplied a handler, so
              the self-serve funnel (which cannot produce this refusal) never grows
              a dead control.
            */}
            {offersPriceConfirmation(failure) && onConfirmPriceChange !== undefined ? (
              <Button
                variant="primary"
                shape="pill"
                size="sm"
                onClick={onConfirmPriceChange}
                disabled={isConfirming}
              >
                {t('shop.estimate.continue.error.priceChanged.action')}
              </Button>
            ) : null}
            {offersPlanShelfReturn(failure) && planShelfPath !== undefined ? (
              <Link to={planShelfPath} className="text-sm font-medium text-primary underline">
                {t('shop.estimate.continue.error.planNotInOffer.action')}
              </Link>
            ) : null}
            <Link to="/#support" className="text-sm font-medium text-primary underline">
              {t('shop.estimate.continue.error.contactSupport')}
            </Link>
          </div>
        )}
      </div>
    </Alert>
  );
}
